import React, { useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { FaShieldAlt } from "react-icons/fa";

const WhyChoose = () => {
  const [activeIndex, setActiveIndex] = useState(2);

  const highlights = [
    "Trusted Expertise In Technology",
    "Commitment To Client Success",
    "Innovative Solutions For Growth",
  ];

  return (
   <section className="relative overflow-hidden bg-black text-white">
  {/* 🌊 Top Wave Transition */}
  <svg
    className="absolute top-0 left-0 w-full z-0"
    viewBox="0 0 1440 320"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#000"
      fillOpacity="1"
      d="M0,160L60,144C120,128,240,96,360,101.3C480,107,600,149,720,165.3C840,181,960,171,1080,149.3C1200,128,1320,96,1380,80L1440,64L1440,0L0,0Z"
    ></path>
  </svg>

  {/* ✨ Premium GOLD + BLACK Gradient Background */}
  <div className="absolute inset-0 z-0 bg-gradient-to-b from-black via-[#1a1a1a] to-black">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,215,0,0.25),_transparent_60%)]"></div>
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(255,215,0,0.18),_transparent_70%)]"></div>
    <div className="absolute inset-0 bg-gradient-to-r from-[#2d2d2d] via-black to-[#1e1e1e] opacity-40 animate-gradientFlow"></div>
  </div>

  {/* 🌟 GOLD Glow Elements */}
  <div className="absolute -z-10 w-[900px] h-[900px] bg-[#d4af37] rounded-full blur-[220px] opacity-10 top-1/2 left-[-200px] animate-float-smooth"></div>
  <div className="absolute -z-10 w-[700px] h-[700px] bg-[#b8860b] rounded-full blur-[200px] opacity-10 bottom-0 right-[-200px] animate-float-smooth"></div>

  {/* ✨ Soft Premium Particles */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <div
        key={i}
        className="absolute bg-[#d4af37] rounded-full opacity-40 blur-[2px]"
        style={{
          width: `${Math.random() * 6 + 2}px`,
          height: `${Math.random() * 6 + 2}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animation: `floatParticle ${6 + Math.random() * 6}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 5}s`,
        }}
      ></div>
    ))}
  </div>

  {/* 🌤 MAIN CONTENT */}
  <div className="relative z-10 py-28 px-6 sm:px-10 lg:px-20 max-w-7xl mx-auto">
    <div className="grid md:grid-cols-2 gap-16 items-center">

      {/* LEFT CONTENT */}
      <div data-aos="fade-right" data-aos-duration="900" className="space-y-8">
        <div>
          <span className="text-sm font-semibold text-[#d4af37] uppercase tracking-widest">
            Why Choose Us
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mt-3 leading-tight">
            Driven By Quality, <br />
            <span className="font-bold" style={{ color: "#d4af37" }}>Focused</span> On Your Digital Growth
          </h2>
          <p className="text-gray-300 mt-5 text-lg max-w-md leading-relaxed">
            Skilled in collaboration and precision — we build adaptable,
            future-ready solutions that empower businesses to grow.
          </p>
        </div>

        {/* HIGHLIGHTS */}
        <div className="flex flex-col gap-4 pt-4">
          {highlights.map((item, idx) => (
            <div
              key={idx}
              className="group flex items-center gap-3 bg-white/5 backdrop-blur-sm p-3 rounded-full shadow-sm hover:shadow-gold transition-all duration-300 hover:-translate-y-0.5"
            >
              <div className="bg-[#d4af37]/20 p-2 rounded-full group-hover:bg-[#d4af37] transition-all duration-300">
                <FiCheckCircle className="text-[#d4af37] group-hover:text-black text-xl" />
              </div>
              <span className="text-gray-200 font-medium group-hover:text-[#d4af37] transition-all duration-300">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT IMAGE */}
      <div
        className="relative flex flex-col justify-center items-center gap-8"
        data-aos="fade-left"
        data-aos-duration="900"
      >
        <div className="relative group">
          <div className="absolute -inset-3 bg-gradient-to-r from-[#d4af37] to-[#b8860b] opacity-30 blur-xl rounded-3xl group-hover:opacity-50 transition-all duration-700"></div>

          <img
            src="/teamworking.jpg"
            alt="Team working illustration"
            className="relative w-full max-w-[680px] rounded-3xl object-cover shadow-2xl transition-all duration-700 ease-in-out group-hover:scale-[1.03] group-hover:shadow-[#d4af37]/30"
          />
        </div>

        {/* WARRANTY BADGE */}
        <div className="relative bg-gradient-to-r from-[#d4af37] to-[#b8860b] text-black font-semibold rounded-2xl p-6 w-fit text-center shadow-xl overflow-hidden group transition-transform duration-500 hover:scale-105 animate-float-smooth">
          <div className="absolute inset-0 bg-black/10 blur-xl opacity-0 group-hover:opacity-30 transition-all duration-500"></div>
          <FaShieldAlt className="text-4xl mx-auto mb-3 animate-bounce-slow" />
          <p className="text-base tracking-wide">Warranty & Quality Assurance</p>
          <p className="text-xs mt-1 opacity-80">Premium quality in every process</p>
        </div>
      </div>
    </div>
  </div>

  {/* ANIMATIONS */}
  <style>{`
    @keyframes floatSmooth {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-8px); }
    }
    .animate-float-smooth {
      animation: floatSmooth 6s ease-in-out infinite;
    }

    @keyframes gradientFlow {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .animate-gradientFlow {
      background-size: 200% 200%;
      animation: gradientFlow 12s ease infinite;
    }

    @keyframes floatParticle {
      0%, 100% { transform: translateY(0px); opacity: 0.6; }
      50% { transform: translateY(-20px); opacity: 1; }
    }

    @keyframes bounceSlow {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-5px); }
    }
    .animate-bounce-slow {
      animation: bounceSlow 3s ease-in-out infinite;
    }
  `}</style>
</section>

  );
};

export default WhyChoose;
